import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InboxIcon from "@mui/icons-material/Inbox";

import { toast } from "react-toastify";
import { schedulingApi, eventTypeColors, eventStatusColors } from "../Components/schedulingApi";
import { GlobalSearchBar } from "../Components/ERPList";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import styles from "./AllEnquiries.module.scss";

const theme = createTheme({
  palette: {
    primary: { main: "#5e72e4" },
    secondary: { main: "#f5365c" },
    background: { default: "#f8f9fe" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

function parseUtcDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const hasTimezone = /[zZ]|[+-]\d{2}:\d{2}$/.test(raw);
  const normalized = hasTimezone ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function eventStartIso(ev) {
  return ev?.start || ev?.start_datetime || null;
}

function formatEventStatusLabel(status) {
  const s = (status || "scheduled").replace(/_/g, " ");
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatEventTypeLabel(t) {
  if (!t) return "—";
  return String(t).replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function EventsListPage() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [events, setEvents] = useState([]);
  const [options, setOptions] = useState({ courses: [], lecturers: [], classrooms: [] });
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const [filters, setFilters] = useState({
    start: "",
    end: "",
    course_id: "",
    lecturer_id: "",
    event_type: "",
  });
  const debouncedFilters = useDebouncedValue(filters, 400);

  const loadOptions = useCallback(async () => {
    try {
      const data = await schedulingApi.getOptions();
      if (data?.error) return;
      setOptions({
        courses: data.courses || [],
        lecturers: data.lecturers || [],
        classrooms: data.classrooms || [],
      });
    } catch (_) {}
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedFilters.start) params.start = debouncedFilters.start;
      if (debouncedFilters.end) params.end = debouncedFilters.end;
      if (debouncedFilters.course_id) params.course_id = debouncedFilters.course_id;
      if (debouncedFilters.lecturer_id) params.lecturer_id = debouncedFilters.lecturer_id;
      if (debouncedFilters.event_type) params.event_type = debouncedFilters.event_type;
      let page = 1;
      const pageSize = 100;
      let hasMore = true;
      const merged = [];

      while (hasMore && page <= 20) {
        const data = await schedulingApi.listEvents({ ...params, page, page_size: pageSize });
        if (data?.error) {
          toast.error(data.error);
          setEvents([]);
          return;
        }
        const chunk = Array.isArray(data?.events)
          ? data.events
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : [];
        merged.push(...chunk);

        if (typeof data?.has_more === "boolean") {
          hasMore = data.has_more;
        } else if (typeof data?.page === "number" && typeof data?.total === "number") {
          hasMore = data.page * pageSize < data.total;
        } else {
          hasMore = chunk.length >= pageSize;
        }
        page += 1;
      }

      setEvents(merged);
    } catch (e) {
      toast.error(e?.message || "Failed to load events");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const groupedByEvent = React.useMemo(() => {
    const byEvent = {};
    events.forEach((e) => {
      const id = e.event_id ?? e.id;
      if (!byEvent[id]) byEvent[id] = [];
      byEvent[id].push(e);
    });
    return Object.entries(byEvent).map(([eventId, arr]) => ({
      event_id: Number(eventId),
      ...arr[0],
      scheduled_count: arr.length,
      first_start: arr.map(eventStartIso).filter(Boolean).sort()[0] || null,
    }));
  }, [events]);

  const searchLower = (debouncedSearch || "").trim().toLowerCase();
  const filteredBySearch = searchLower
    ? groupedByEvent.filter((e) => {
        const title = (e.title || "").toLowerCase();
        const course = (e.course?.name || "").toLowerCase();
        const lecturer = `${e.lecturer?.first_name || ""} ${e.lecturer?.last_name || ""}`.toLowerCase();
        const room = (e.classroom?.room_code || "").toLowerCase();
        const type = (e.event_type || "").toLowerCase();
        const status = (e.status || "").toLowerCase().replace(/_/g, " ");
        return (
          title.includes(searchLower) ||
          course.includes(searchLower) ||
          lecturer.includes(searchLower) ||
          room.includes(searchLower) ||
          type.includes(searchLower) ||
          status.includes(searchLower) ||
          String(e.event_id).includes(searchLower)
        );
      })
    : groupedByEvent;

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };
  const getSortedData = () => {
    if (!sortConfig.key) return filteredBySearch;
    return [...filteredBySearch].sort((a, b) => {
      let aVal =
        sortConfig.key === "first_start" || sortConfig.key === "start"
          ? (() => {
              const iso = a.first_start || eventStartIso(a);
              if (!iso) return 0;
              const t = new Date(iso).getTime();
              return Number.isNaN(t) ? 0 : t;
            })()
          : (a[sortConfig.key] ?? "");
      let bVal =
        sortConfig.key === "first_start" || sortConfig.key === "start"
          ? (() => {
              const iso = b.first_start || eventStartIso(b);
              if (!iso) return 0;
              const t = new Date(iso).getTime();
              return Number.isNaN(t) ? 0 : t;
            })()
          : (b[sortConfig.key] ?? "");
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };
  const displayData = getSortedData();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setGlobalSearch("");
    setFilters({
      start: "",
      end: "",
      course_id: "",
      lecturer_id: "",
      event_type: "",
    });
    loadEvents();
    toast.info("Filters cleared.");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
            <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Sessions dashboard
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Click a session to open its profile. Filter by course, lecturer, date and type.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box className={styles.header}>
                <GlobalSearchBar
                  value={globalSearch}
                  onChange={setGlobalSearch}
                  placeholder="Search title, course, lecturer, room…"
                />
                <Box className={styles.headerActions}>
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterOpen(!filterOpen)}
                    variant="outlined"
                    size="small"
                  >
                    {filterOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => navigate("/schedule")}
                  >
                    Open Calendar
                  </Button>
                </Box>
              </Box>

              {filterOpen && (
                <Fade in={filterOpen}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField fullWidth size="small" label="From" type="date" name="start" value={filters.start} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField fullWidth size="small" label="To" type="date" name="end" value={filters.end} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Course</InputLabel>
                          <Select name="course_id" value={filters.course_id} label="Course" onChange={handleInputChange}>
                            <MenuItem value="">All</MenuItem>
                            {(options.courses || []).map((c) => (
                              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Lecturer</InputLabel>
                          <Select name="lecturer_id" value={filters.lecturer_id} label="Lecturer" onChange={handleInputChange}>
                            <MenuItem value="">All</MenuItem>
                            {(options.lecturers || []).map((u) => (
                              <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select name="event_type" value={filters.event_type} label="Type" onChange={handleInputChange}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="class">Class</MenuItem>
                            <MenuItem value="exam">Exam</MenuItem>
                            <MenuItem value="seminar">Seminar</MenuItem>
                            <MenuItem value="unit">Unit</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} className={styles.filterActions}>
                        <Button variant="contained" startIcon={<SearchIcon />} onClick={() => loadEvents()}>
                          Search
                        </Button>
                        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                          Reset
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Fade>
              )}

              {loading && (
                <Box className={styles.loadingContainer}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={400} />
                </Box>
              )}

              {!loading && displayData.length === 0 && (
                <Box className={styles.emptyState}>
                  <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No sessions found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Adjust filters or open the calendar to create sessions.
                  </Typography>
                </Box>
              )}

              {!loading && displayData.length > 0 && (
                <>
                  <Paper variant="outlined" className={styles.tableContainer}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel active={sortConfig.key === "title"} direction={sortConfig.key === "title" ? sortConfig.direction : "asc"} onClick={() => requestSort("title")}>
                              Title
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Course</TableCell>
                          <TableCell>Lecturer</TableCell>
                          <TableCell>Room</TableCell>
                          <TableCell>
                            <TableSortLabel active={sortConfig.key === "first_start"} direction={sortConfig.key === "first_start" ? sortConfig.direction : "asc"} onClick={() => requestSort("first_start")}>
                              First start
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Sessions</TableCell>
                          <TableCell>Students</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayData.map((ev) => (
                          <TableRow
                            key={ev.event_id}
                            hover
                            onClick={() => navigate(`/schedule/events/${ev.event_id}`)}
                            className={styles.tableRow}
                          >
                            <TableCell>
                              <Typography variant="body2" className={styles.primaryText}>{ev.title}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={formatEventTypeLabel(ev.event_type)}
                                size="small"
                                sx={{
                                  bgcolor: eventTypeColors[ev.event_type] || "#666",
                                  color: "#fff",
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={formatEventStatusLabel(ev.status)}
                                size="small"
                                sx={
                                  eventStatusColors[ev.status || "scheduled"]
                                    ? {
                                        bgcolor: eventStatusColors[ev.status || "scheduled"],
                                        color: "#fff",
                                      }
                                    : { bgcolor: "#e5e7eb", color: "#374151" }
                                }
                              />
                            </TableCell>
                            <TableCell>{ev.course?.name || "—"}</TableCell>
                            <TableCell>{ev.lecturer ? `${ev.lecturer.first_name} ${ev.lecturer.last_name}` : "—"}</TableCell>
                            <TableCell>{ev.classroom?.room_code || "—"}</TableCell>
                            <TableCell>
                              {(() => {
                                const iso = ev.first_start || eventStartIso(ev);
                                if (!iso) return "—";
                                const d = parseUtcDate(iso) || new Date(iso);
                                return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
                              })()}
                            </TableCell>
                            <TableCell>{ev.scheduled_count ?? 0}</TableCell>
                            <TableCell>{ev.student_count ?? 0} / {ev.capacity ?? 0}</TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Button size="small" variant="outlined" onClick={() => navigate(`/schedule/events/${ev.event_id}`)}>
                                Open
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                  <Box className={styles.pagination}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {displayData.length} of {displayData.length} results
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
      </Box>
    </ThemeProvider>
  );
}
